import moment from 'moment';
import {
  NEXTSTATUS,
  PA_GENERAL_STATUS_MAPPER_SUBSTATUS,
  PA_GENERAL_STATUS_SUBSTATUS_REPLACEMENT,
  PA_GENERAL_STATUS_SUBSTATUS_WITH_COMMA_LENGTH,
  POST_APP_STATUS_MAP,
  POST_ISSUE_STATUS_MAP,
  PRE_APP_STATUS_MAP,
  STATUSES,
} from '../static/AccountCenterStatusMap';

import {
  AnnuitySummaryInfo,
  REGISTERED_WORKFLOW_STATES,
  REGISTERED_WORKFLOW_SUB_STATES,
} from '../static/AnnuityStatusTypes';

import {
  POST_ISSUE_STATUS_MAP as ANNUITY_POST_ISSUE_STATUS_MAP,
  POST_SUBMIT_STATUS_MAP as ANNUITY_POST_SUBMIT_STATUS_MAP,
  PRE_SUBMIT_STATES as ANNUITY_PRE_SUBMIT_STATES,
  PRE_SUBMIT_STATUS_MAP as ANNUITY_PRE_SUBMIT_STATUS_MAP,
  WORKFLOW_STATE_TO_STATUS_MAP,
} from '../static/AnnuityStatusMap';
import { AccountCenterListRecordView, ClientAnnuityContractStatus, ClientPolicyStatus } from '../types/Types';
import { Utils } from '../../../../common/js/Utils';
import { Assumptions } from '../../../../common/js/static/Assumptions';
import { RateClasses } from '../../../../common/js/static/RateClasses';
import { Person } from '../../../../common/js/customer/Person';
import { BaseQuote } from '../../../../common/js/quote/BaseQuote';
import { PolicyAdminStatus } from '../../../../common/js/policyAdmin/PolicyAdminStatus';
import { WorkflowHelper } from '../../../../coverpath-common/classes/WorkflowHelper';
import { ExamStatus } from '@hit/underwriting-api-client';
import { SecurityUtils } from '../../../../coverpath-common/js/static/SecurityUtils';
import { isAgentEsignRequired } from './AgentUtilitiesShared';

/**
 * given a policy that has a premium and a term, estimate the comission of sale
 * for an agent for year 1
 * @param premium
 * @param term
 * @returns {Array<number>}
 */
export function getCommission(premium: number, term: number): Array<string> {
  if (!premium) {
    return null;
  }

  switch (term.valueOf()) {
    case 10:
      return [
        Utils.formatCurrency(((premium - 6.5) / 1.044) * 12 * 0.4), // Year 1
        Utils.formatCurrency(((premium - 6.5) / 1.044) * 12 * 0.1), // Year 2
      ];
    case 20:
      return [
        Utils.formatCurrency(((premium - 6.5) / 1.044) * 12 * 0.45), // Year 1
        Utils.formatCurrency(((premium - 6.5) / 1.044) * 12 * 0.12), // Year 2
      ];
    default:
      return null;
  }
}

export async function mapPreIssueStatus(status: ClientPolicyStatus, policyInfo) {
  if (!policyInfo.submittedAt) {
    await mapPreAppStatus(status, policyInfo);
  } else {
    await mapPostAppStatus(status, policyInfo);
  }

  /**
   * fall back to the old matching logic for no-matches
   */
  if (!status.summaryStatus) {
    await getStatusInfoOld(status, policyInfo);
  }

  /**
   * catch no-matches, if still any, and put them under "App Submitted" status. This is to deal with edge cases.
   * For example, an application was submitted at a time when workflow was down, therefore the workflowState and workflowSubState would be null.
   */
  if (!status.summaryStatus) {
    status.summaryStatus = STATUSES.submitted;
  }
}

export async function mapAnnuityStatus(status: ClientAnnuityContractStatus, policyInfo: AnnuitySummaryInfo) {
  const isInPostSubmitStage = policyInfo.workflowState && policyInfo.workflowSubState;
  const isInPostIssueStage = policyInfo.workflowState === REGISTERED_WORKFLOW_STATES.IN_FORCE;

  if (isInPostIssueStage) {
    await mapPostIssueAnnuityStatus(status, policyInfo);
  } else if (isInPostSubmitStage) {
    await mapPostAppAnnuityStatus(status, policyInfo);
  } else {
    await mapPreAppAnnuityStatus(status, policyInfo);
  }
}

export async function mapPreAppAnnuityStatus(status: ClientAnnuityContractStatus, policyInfo: AnnuitySummaryInfo) {
  const {
    applicationFormQsID,
    suitabilityFormQsID,
    replacementFormQsID,
    cotFormQsID,
    fR3013FormQsID,
    workflowState,
    workflowSubState,
    policyNumber,
  } = policyInfo;

  const applicationStateFilterData = {
    applicationFormQsID,
    suitabilityFormQsID,
    replacementFormQsID,
    cotFormQsID,
    fR3013FormQsID,
    workflowState,
    workflowSubState,
    policyNumber,
  };

  const preSubmitState = ANNUITY_PRE_SUBMIT_STATES.find((state) => state.match(applicationStateFilterData));
  const statusMap = ANNUITY_PRE_SUBMIT_STATUS_MAP[preSubmitState.state];
  status.summaryStatus = statusMap.desc;
  status.actions = statusMap.actions;
  status.applicationStage = statusMap.stage;
}

export async function mapPostAppAnnuityStatus(status: ClientAnnuityContractStatus, policyInfo: AnnuitySummaryInfo) {
  const isStateRegistered = Object.values(REGISTERED_WORKFLOW_STATES).includes(
    <REGISTERED_WORKFLOW_STATES>policyInfo.workflowState
  );
  const isSubStateRegistered = Object.values(REGISTERED_WORKFLOW_SUB_STATES).includes(
    <REGISTERED_WORKFLOW_SUB_STATES>policyInfo.workflowSubState
  );

  const postSubmitState =
    isStateRegistered && isSubStateRegistered
      ? WORKFLOW_STATE_TO_STATUS_MAP[policyInfo.workflowState][policyInfo.workflowSubState]
      : // Fallback for workflow states that don't have corresponding list view statuses
        WORKFLOW_STATE_TO_STATUS_MAP['CATCH_ALL']['PRE_ISSUE'];

  const statusMap = ANNUITY_POST_SUBMIT_STATUS_MAP[postSubmitState];
  status.summaryStatus = statusMap.desc;
  status.actions = statusMap.actions;
  status.applicationStage = statusMap.stage;
  if (typeof statusMap.subStatus === 'function') {
    status.subStatus = await statusMap.subStatus(policyInfo);
  }
}

export async function mapPostIssueAnnuityStatus(status: ClientAnnuityContractStatus, policyInfo: AnnuitySummaryInfo) {
  const isStateRegistered = Object.values(REGISTERED_WORKFLOW_STATES).includes(
    <REGISTERED_WORKFLOW_STATES>policyInfo.workflowState
  );
  const isSubStateRegistered = Object.values(REGISTERED_WORKFLOW_SUB_STATES).includes(
    <REGISTERED_WORKFLOW_SUB_STATES>policyInfo.workflowSubState
  );

  const postIssueState =
    isStateRegistered && isSubStateRegistered
      ? WORKFLOW_STATE_TO_STATUS_MAP[policyInfo.workflowState][policyInfo.workflowSubState]
      : // Fallback for workflow states that don't have corresponding list view statuses
        WORKFLOW_STATE_TO_STATUS_MAP['CATCH_ALL']['POST_ISSUE'];

  const statusMap = ANNUITY_POST_ISSUE_STATUS_MAP[postIssueState];
  status.summaryStatus = statusMap.desc;
  status.actions = statusMap.actions;
  status.applicationStage = statusMap.stage;
  if (typeof statusMap.subStatus === 'function') {
    status.subStatus = await statusMap.subStatus(policyInfo);
  }
}

/**
 * Map Policy Admin status to Coverpath agent account center status.
 *
 * @param {ClientPolicyStatus, PolicyAdminStatus, policyStageMapper: string} clientPolicyStatus, paStatus, policyStageMapper
 */
export function mapPostIssueStatus(
  clientPolicyStatus: ClientPolicyStatus,
  paStatus: PolicyAdminStatus,
  policyStageMapper: string,
  policyConvertedTo: string,
  policyConvertedToIsInForce: boolean
) {
  let appendSubStatus = '';
  /*
  We append PA_GENERAL_STATUS_MAPPER_SUBSTATUS to the end of the exisiting policyStageMapper.
  To handle it, we need to split policyStageMapper with ':' and get the last element.
  If the element is in PA_GENERAL_STATUS_MAPPER_SUBSTATUS, then we separate the policyStageMapper and appendSubstatus.
   */
  if (policyStageMapper.indexOf(':') > -1) {
    const mapperArray = policyStageMapper.split(':');
    if (mapperArray[mapperArray.length - 1] in PA_GENERAL_STATUS_MAPPER_SUBSTATUS) {
      policyStageMapper = policyStageMapper.substr(
        0,
        policyStageMapper.length - mapperArray[mapperArray.length - 1].length - 1
      );
      appendSubStatus = PA_GENERAL_STATUS_MAPPER_SUBSTATUS[mapperArray[mapperArray.length - 1]];
    }
  }
  clientPolicyStatus.important = POST_ISSUE_STATUS_MAP[policyStageMapper].important;
  clientPolicyStatus.summaryStatus = POST_ISSUE_STATUS_MAP[policyStageMapper].status;

  const subStatusContent = POST_ISSUE_STATUS_MAP[policyStageMapper].subStatus;
  clientPolicyStatus.importantIfLessThanDays = POST_ISSUE_STATUS_MAP[policyStageMapper].importantIfLessThanDays;

  if (typeof subStatusContent === 'function') {
    clientPolicyStatus.subStatus = subStatusContent.call(this, policyConvertedTo, policyConvertedToIsInForce);
  } else {
    clientPolicyStatus.subStatus = subStatusContent;
  }
  /*
  If there is an appendSubStatus, we need to replace or append it to substatus.
   */
  if (appendSubStatus) {
    if (appendSubStatus.indexOf(PA_GENERAL_STATUS_SUBSTATUS_REPLACEMENT) > -1) {
      clientPolicyStatus.subStatus = clientPolicyStatus.subStatus
        ? appendSubStatus.replace(PA_GENERAL_STATUS_SUBSTATUS_REPLACEMENT, clientPolicyStatus.subStatus)
        : appendSubStatus.substr(PA_GENERAL_STATUS_SUBSTATUS_WITH_COMMA_LENGTH);
    } else {
      clientPolicyStatus.subStatus = appendSubStatus;
      clientPolicyStatus.important = false;
      clientPolicyStatus.actionRequiredDate = null;
    }
  }
  const nextStatusContent = POST_ISSUE_STATUS_MAP[policyStageMapper].nextStatus;

  if (typeof nextStatusContent === 'function') {
    clientPolicyStatus.nextStatus = nextStatusContent.call(this, policyConvertedTo);
  } else {
    clientPolicyStatus.nextStatus = nextStatusContent;
  }
  const actionRequiredDate = POST_ISSUE_STATUS_MAP[policyStageMapper].actionRequiredDate;
  if (typeof actionRequiredDate === 'function') {
    clientPolicyStatus.actionRequiredDate = actionRequiredDate.call(this, paStatus);
  }
  clientPolicyStatus.stageBillingStatus = paStatus.getWorkflowEquivalentStage();
  if (!clientPolicyStatus.stageBillingStatus) {
    // For BR2 stage, since its mapping only exist on cp-agent only
    clientPolicyStatus.stageBillingStatus = paStatus.getWorkflowEquivalentStageForCoverpathAgent();
  }
  clientPolicyStatus.paCaseDetailsStatus = paStatus.getWorkflowEquivalentStageForCaseDetails();
  clientPolicyStatus.graceEndDate = paStatus.getGraceEndDate();
  clientPolicyStatus.faceAmount = paStatus.getFaceAmount();
  clientPolicyStatus.conversionEndDate = paStatus.getConversionDate();
  clientPolicyStatus.conversionCreditPercent = paStatus.getConversionCreditPercent();
  clientPolicyStatus.conversionCreditExpirationDate = paStatus.getConversionCreditExpirationDate();
  clientPolicyStatus.acceleratedBenefitRider = paStatus.getAcceleratedBenefitRider();
  clientPolicyStatus.nextPaymentDate = paStatus.getNextPaymentDate();
  clientPolicyStatus.paidToDate = paStatus.getPaidToDate();
  clientPolicyStatus.effectiveDate = paStatus.getEffectiveDate();
  clientPolicyStatus.tlicEffectiveDate = paStatus.getTlicEffectiveDate();
  clientPolicyStatus.freeLookEndDate = paStatus.getFreeLookEndDate();
  clientPolicyStatus.hasWaiverOfPremium = paStatus.getWaiverOfPremium();
  clientPolicyStatus.rateClass = paStatus.getRateClass();
  clientPolicyStatus.issuedDate = paStatus.getIssuedDate();
  clientPolicyStatus.paTerm = paStatus.getDuration();
  clientPolicyStatus.paTotalPremium = paStatus.getTotalPremium();
  clientPolicyStatus.paymentMethod = paStatus.getPaymentType();
  clientPolicyStatus.paPaymentFrequency = paStatus.getPaymentFequency();
  clientPolicyStatus.paPolicyId = paStatus.getPolicyAdminPolicyId();
  clientPolicyStatus.paOwners = paStatus.owners;
  clientPolicyStatus.paIsOwnerEntity = paStatus.getOwnerIsEntity();
  clientPolicyStatus.paInsured = paStatus.insured;
  clientPolicyStatus.paRiders = paStatus.policyAdminRiders;
  const alirInfo = clientPolicyStatus.paRiders?.getALIRRiderInfo();
  if (alirInfo) {
    clientPolicyStatus.paTotalPremium += alirInfo.premium;
  }
}
/**
 * Keeping the original function because the new account status requirements do not cover all possible cases (yet)
 * and we need to fallback to the old account center statuses for any applications that are not matched under the new requirements.
 */
export function getStatusInfoOld(status: ClientPolicyStatus, resultRow) {
  if (!resultRow.submittedAt) {
    if (resultRow.accountCreated) {
      status.summaryStatus = STATUSES.started;
    } else if (resultRow.linkSentDate) {
      status.summaryStatus = STATUSES.unopened;
      if (resultRow.isAgentAssistant && resultRow.linkBeingResent) {
        status.summaryStatus = STATUSES.advisorDraft;
      }

      if (resultRow.linkOpenedDate) {
        status.summaryStatus = STATUSES.opened;
      }
    } else if (resultRow.isAgentAssistant) {
      status.summaryStatus = STATUSES.advisorDraft;
    } else {
      status.summaryStatus = STATUSES.created;
    }
  } else {
    status.agentEsignRequired = isAgentEsignRequired(resultRow);

    // Accelerated Application = AU1-AU4
    if (resultRow.workflowState === 'Accelerated Application') {
      // Waiting for Acceptance = AU2
      if (resultRow.workflowSubState === 'Waiting for Acceptance') {
        status.summaryStatus = STATUSES.submitted;
      }
      // Waiting for Signature = AU3
      else if (resultRow.workflowSubState === 'Waiting for Signature') {
        status.summaryStatus = STATUSES.submitted;
      }
      // Determine Underwriting Class = AU1 happens instantly, no status necessary
      // Issuing Policy = AU4 happens instantly, no status necessary
    }
    // License-verification = CS2-CS3
    else if (resultRow.workflowState === 'License-verification') {
      status.summaryStatus = STATUSES.advisorCheck;
    }
    // TLIC Application = HA1, VI1-VI6, DG1, VL1-VL2, IU1, VE1, TC1-TC2, ES1
    // Haven Application = MA1-MA3
    //
    // Waiting for Acceptance = TC1, AU2
    // Waiting for Signature = AU3, ES1, TC2, MA2, ES1
    // Waiting for Application Acceptance = MA1
    else if (
      (resultRow.workflowState === 'TLIC Application' || resultRow.workflowState === 'Haven Application') &&
      (resultRow.workflowSubState === 'Waiting for Acceptance' ||
        resultRow.workflowSubState === 'Waiting for Signature' ||
        resultRow.workflowSubState === 'Waiting for Application Acceptance' ||
        resultRow.workflowSubState === 'Correcting SSN' ||
        resultRow.workflowSubState === 'Correcting Insured SSN' ||
        resultRow.workflowSubState === 'Correcting Owner SSN' ||
        resultRow.workflowSubState === 'Correcting License')
    ) {
      // SSN Verification = VI4 - VI6
      if (
        resultRow.workflowSubState === 'Correcting SSN' ||
        resultRow.workflowSubState === 'Correcting Insured SSN' ||
        resultRow.workflowSubState === 'Correcting Owner SSN'
      ) {
        status.summaryStatus = STATUSES.ssnVerification;
      }
      //driver's license = VL1
      else if (resultRow.workflowSubState === 'Correcting License') {
        status.summaryStatus = STATUSES.driverLicenseIssue;
      } else if (resultRow.workflowSubState === 'Waiting for Signature') {
        if (isAgentEsignRequired(resultRow)) {
          status.summaryStatus = STATUSES.advisorSignature;
        } else {
          status.summaryStatus = STATUSES.submitted;
        }
      } else {
        status.summaryStatus = STATUSES.submitted;
      }
    }
    // Waiting for Labs = MA1, MA2, TC3, ES2
    else if (resultRow.workflowSubState === 'Waiting for Labs') {
      status.summaryStatus = STATUSES.labsSched;
    }

    // TLIC Issued TC3, FU1-FU3, ES2
    // Haven Application = MA1-MA3
    //
    // Waiting for Policy Acceptance = FU2
    else if (
      (resultRow.workflowState === 'TLIC Issued' || resultRow.workflowState === 'Haven Application') &&
      !(resultRow.workflowSubState === 'Waiting for Policy Acceptance')
    ) {
      status.summaryStatus = STATUSES.labsReceived;
    }
    // Manual Underwriting = FU2, MU3-MU4
    else if (isUnderReview(resultRow.workflowState, resultRow.workflowSubState)) {
      status.summaryStatus = STATUSES.underReview;

      // Awaiting APS = FU3, MU3-MU4
      if (resultRow.workflowSubState === 'Awaiting APS') {
        status.summaryStatus = STATUSES.aps;
      }
    }
    // Waiting for Policy Acceptance = FU2
    // Waiting for Issue Acceptance = MU5
    else if (
      (resultRow.workflowSubState === 'Waiting for Policy Acceptance' ||
        resultRow.workflowSubState === 'Waiting for Issue Acceptance') &&
      resultRow.offerRequiredBy
    ) {
      status.summaryStatus = STATUSES.knockout;
    }
    // In Force = IF1-IF3
    else if (resultRow.workflowState === 'In Force') {
      if (resultRow.workflowSubState === 'Free Look Period' || resultRow.workflowSubState === 'Pending Policy Docs') {
        status.summaryStatus = STATUSES.free;
      }
      // Issued = IF3
      else if (resultRow.workflowSubState === 'Issued') {
        if (resultRow.status === 'LapsePending') {
          status.summaryStatus = STATUSES.grace;
        } else {
          status.summaryStatus = STATUSES.inForce;
        }
      }
    }
    // Terminated = TM1-TM5
    //
    // Lapsed = IF3, TM1
    else if (resultRow.workflowState === 'Terminated' && resultRow.workflowSubState === 'Lapsed') {
      if (resultRow.canBeReinstatedUntil) {
        const lapsedPlus60 = moment(resultRow.canBeReinstatedUntil).add(60, 'days');

        if (lapsedPlus60.isAfter(moment()) || lapsedPlus60.isSame(moment())) {
          status.summaryStatus = STATUSES.lapsedLess;
        } else {
          status.summaryStatus = STATUSES.lapsedGreater;
        }
      } else {
        status.summaryStatus = STATUSES.lapsed;
      }
    }
    // Application Rejected = DN1-DN3
    else if (resultRow.workflowState === 'Application Rejected') {
      status.summaryStatus = STATUSES.ineligible;
    }
    // Application Declined = DN4-DN5
    else if (resultRow.workflowState === 'Application Declined') {
      status.summaryStatus = STATUSES.declined;
    }
    // Application Canceled By User = CN1-CN2
    else if (resultRow.workflowState === 'Application Canceled By User') {
      setCancelledText();
    }
    // Application Canceled = CN3-CN6
    else if (resultRow.workflowState === 'Application Canceled') {
      setCancelledText();
    }
  }

  function setCancelledText() {
    status.summaryStatus = STATUSES.canceledByClient;
  }

  function isUnderReview(workflowState: string, workflowSubState: string) {
    return (
      workflowState === 'Manual Underwriting' ||
      (workflowState === 'Underwriting' && workflowSubState === 'Underwriting')
    );
  }
}

export function validateCompleteQuote(insuranceQuote: BaseQuote): boolean {
  const person = insuranceQuote.customer.persons[0] as Person;
  if (
    !!person.firstName &&
    !!person.lastName &&
    !!person.email &&
    !!insuranceQuote.dateOfBirth &&
    !!insuranceQuote.term &&
    !!insuranceQuote.faceAmount &&
    !!insuranceQuote.healthClass &&
    !!insuranceQuote.contractState &&
    !!insuranceQuote.gender
  ) {
    const age = Utils.getAgeFromBirthday(insuranceQuote.dateOfBirth.toLocaleDateString());
    if (age > 17) {
      return true;
    }
  }
  return false;
}

//=================================
// private helper functions - not exported.

function calculatePremium(face: number, base: number, waiver: number, flatExtra: number): number {
  const thousands = (face / 1e3) | 0;

  return base + waiver + (thousands * flatExtra) / 12;
}

export async function mapPreAppStatus(status: ClientPolicyStatus, policyInfo) {
  /**
   * Sequence of checking each stage is important.
   */
  let statusObject = null;
  if (policyInfo.accountCreated) {
    // when link already sent or link not sent but is not agent assist
    // then it must be either personalized application or generic application
    if (policyInfo.linkSentDate || !policyInfo.isAgentAssistant) {
      if (policyInfo.progressPercentage) {
        if (policyInfo.progressPercentage === 1) {
          status.summaryStatus = PRE_APP_STATUS_MAP.appCompleted.desc;
          status.subStatus = PRE_APP_STATUS_MAP.appCompleted.subStatus;
          statusObject = PRE_APP_STATUS_MAP.appCompleted;
        } else {
          statusObject = PRE_APP_STATUS_MAP.appStarted;
          status.summaryStatus = PRE_APP_STATUS_MAP.appStarted.desc;
        }
      } else {
        statusObject = PRE_APP_STATUS_MAP.acctCreated;
        status.summaryStatus = PRE_APP_STATUS_MAP.acctCreated.desc;
        status.subStatus = PRE_APP_STATUS_MAP.acctCreated.subStatus;
      }
    } else {
      //if link not sent yet and is agent assist, then must be under agent draft
      statusObject = PRE_APP_STATUS_MAP.draftStarted;
      status.summaryStatus = PRE_APP_STATUS_MAP.draftStarted.desc;
      status.subStatus = PRE_APP_STATUS_MAP.draftStarted.subStatus;
    }
  } else if (policyInfo.linkOpenedDate) {
    statusObject = PRE_APP_STATUS_MAP.linkOpened;
    status.summaryStatus = PRE_APP_STATUS_MAP.linkOpened.desc;
    status.subStatus = PRE_APP_STATUS_MAP.linkOpened.subStatus;
  } else if (policyInfo.linkSentDate) {
    if (policyInfo.isAgentAssistant && policyInfo.linkBeingResent) {
      statusObject = PRE_APP_STATUS_MAP.draftStarted;
      status.summaryStatus = PRE_APP_STATUS_MAP.draftStarted.desc;
    } else {
      statusObject = PRE_APP_STATUS_MAP.linkCreated;
      status.summaryStatus = PRE_APP_STATUS_MAP.linkCreated.desc;
      status.subStatus = PRE_APP_STATUS_MAP.linkCreated.subStatus;
    }
  } else if (policyInfo.isAgentAssistant) {
    statusObject = PRE_APP_STATUS_MAP.draftStarted;
    status.summaryStatus = PRE_APP_STATUS_MAP.draftStarted.desc;
    status.subStatus = PRE_APP_STATUS_MAP.draftStarted.subStatus;
  }

  if (policyInfo.progressPercentage !== null && policyInfo.progressPercentage !== undefined) {
    status.summaryStatus = status.summaryStatus.replace(
      '[progress_percent]',
      Math.round(policyInfo.progressPercentage * 100).toString()
    );
  } else {
    status.summaryStatus = status.summaryStatus.replace('([progress_percent]% filled)', '');
  }
  if (statusObject) {
    status.nextStatus = statusObject.nextStatus;
  }
}

export async function mapPostAppStatus(status: ClientPolicyStatus, policyInfo) {
  status.isPostApp = true;
  if (
    !policyInfo.workflowState ||
    !policyInfo.workflowSubState ||
    !POST_APP_STATUS_MAP[policyInfo.workflowState] ||
    !POST_APP_STATUS_MAP[policyInfo.workflowState][policyInfo.workflowSubState]
  ) {
    return;
  }

  status.agentEsignRequired = isAgentEsignRequired(policyInfo);

  const statusContent = POST_APP_STATUS_MAP[policyInfo.workflowState][policyInfo.workflowSubState].desc;
  if (typeof statusContent === 'function') {
    status.summaryStatus = statusContent.call(this, policyInfo);
  } else {
    status.summaryStatus = statusContent;
  }

  const subStatusContent = POST_APP_STATUS_MAP[policyInfo.workflowState][policyInfo.workflowSubState].subStatus;
  if (typeof subStatusContent === 'function') {
    status.subStatus = subStatusContent.call(this, policyInfo);
  } else {
    status.subStatus = subStatusContent;
  }

  const nextStatusContent = POST_APP_STATUS_MAP[policyInfo.workflowState][policyInfo.workflowSubState].nextStatus;
  if (typeof nextStatusContent === 'function') {
    status.nextStatus = nextStatusContent.call(this, policyInfo);
  } else {
    status.nextStatus = nextStatusContent;
  }

  status.importantIfLessThanDays =
    POST_APP_STATUS_MAP[policyInfo.workflowState][policyInfo.workflowSubState].importantIfLessThanDays;

  const activityName: string = POST_APP_STATUS_MAP[policyInfo.workflowState][policyInfo.workflowSubState].activityName;
  if (activityName) {
    const activityExecutionTime: Date = await WorkflowHelper.getScheduledActivityExecutionTime(
      activityName,
      policyInfo.aid,
      'agent'
    );
    const actionRequiredDate =
      POST_APP_STATUS_MAP[policyInfo.workflowState][policyInfo.workflowSubState].actionRequiredDate;
    if (activityExecutionTime && typeof actionRequiredDate === 'function') {
      status.actionRequiredDate = actionRequiredDate.call(this, activityExecutionTime);
    } else {
      status.subStatus = '';
    }
  } else if (
    policyInfo.workflowState === 'Advisor Amendment' &&
    policyInfo.workflowSubState === 'Waiting for Advisor Amendment'
  ) {
    const obj: any = await WorkflowHelper.getCurrentStageExecutionStage(policyInfo.aid, 'agent');
    const activityExecutionTime = obj.startTime;
    const actionRequiredDate =
      POST_APP_STATUS_MAP[policyInfo.workflowState][policyInfo.workflowSubState].actionRequiredDate;
    if (activityExecutionTime && typeof actionRequiredDate === 'function') {
      const offSet = policyInfo.holdTime
        ? Assumptions.autoCancelPolicyAfterHoldDays
        : Assumptions.autoSendPolicyToCustomerWithoutAmendmentDays;
      status.actionRequiredDate = actionRequiredDate.call(this, activityExecutionTime, offSet);
    } else {
      status.subStatus = '';
    }
  } else if (policyInfo.workflowState === 'Funding Pause' && policyInfo.workflowSubState === 'Funds have arrived') {
    const agentEsignCompleteDateUw = policyInfo.agentEsignCompleteDateUw;
    const actionRequiredDate =
      POST_APP_STATUS_MAP[policyInfo.workflowState][policyInfo.workflowSubState].actionRequiredDate;
    if (agentEsignCompleteDateUw && typeof actionRequiredDate === 'function') {
      status.actionRequiredDate = actionRequiredDate.call(
        this,
        agentEsignCompleteDateUw,
        Assumptions.autoCancelPolicyAfterAdvisorAppPacketEsign
      );
    } else {
      status.subStatus = '';
    }
  }
  //todo: we should not handle this specially, https://massmutual.atlassian.net/browse/HL-16981 would fix this
  else if (
    policyInfo.workflowState === 'Haven Application' &&
    policyInfo.workflowSubState === 'Waiting for Advisor Signature'
  ) {
    const tempDate = new Date(policyInfo.workflowStateUpdate);
    const activityExecutionTime = new Date(tempDate.setDate(tempDate.getDate() + Assumptions.advisorReplacementEsign));
    const actionRequiredDate =
      POST_APP_STATUS_MAP[policyInfo.workflowState][policyInfo.workflowSubState].actionRequiredDate;
    if (activityExecutionTime && typeof actionRequiredDate === 'function') {
      status.actionRequiredDate = actionRequiredDate.call(this, activityExecutionTime);
    } else {
      status.subStatus = '';
    }
  } else if (policyInfo.workflowSubState === 'Waiting for Labs') {
    const activityExecutionTime = policyInfo.paramedScheduledDate || policyInfo.paramedRequiredBy;
    const actionRequiredDate =
      POST_APP_STATUS_MAP[policyInfo.workflowState][policyInfo.workflowSubState].actionRequiredDate;
    if (activityExecutionTime && typeof actionRequiredDate === 'function') {
      status.actionRequiredDate = actionRequiredDate.call(this, activityExecutionTime);
    } else {
      status.subStatus = '';
    }
  }

  const rateClassType = POST_APP_STATUS_MAP[policyInfo.workflowState][policyInfo.workflowSubState].rateClassType;

  if (rateClassType) {
    status.summaryStatus = handleRatingClass(status.summaryStatus, policyInfo[rateClassType]);
    status.subStatus = handleRatingClass(status.subStatus, policyInfo[rateClassType]);
  }

  //TODO: Remove this code when we start using insurance_quote for conversion
  //And use insurance_quotes for conversion
  //The problem here is, its populating post issue object as if the
  //faceAmount, term, totalPremium, policyNumber & paymentFrequency is coming from PA
  status.faceAmount = policyInfo.faceAmount;
  status.term = policyInfo.term;
  status.totalPremium = policyInfo.totalPremium;
  status.policyNumber = policyInfo.policyNumber;
  status.paymentFrequency = policyInfo.paymentFrequency;
}

export function handleRatingClass(value: string, rateClass: string): string {
  let newValue: string;
  const rateClassDesc = RateClasses.find(rateClass)['desc'];
  if (rateClassDesc && rateClassDesc !== 'N/A') {
    newValue = value.replace(new RegExp(/\[rate_class\]/), rateClassDesc);
  } else {
    newValue = value.replace(new RegExp(/: \[rate_class\]/), '');
  }
  return newValue;
}

// Check if the policy is currently in paramed stage
export function isParamedInProgress(status: ClientPolicyStatus, inLiteTouch: boolean): boolean {
  return isInParamed(status.workflowState, status.workflowSubState) && !inLiteTouch;
}

// Check if the policy is currently in underwriting stage
export function isUnderwritingInProgress(status: ClientPolicyStatus, inLiteTouch: boolean): boolean {
  return isInUnderwriting(status) || (isInParamed(status.workflowState, status.workflowSubState) && inLiteTouch);
}

export function isInParamed(workflowState: string, workflowSubState: string): boolean {
  return (
    (workflowState === 'Haven Application' || workflowState === 'TLIC Issued') &&
    workflowSubState === 'Waiting for Labs'
  );
}

function isInUnderwriting(status: ClientPolicyStatus): boolean {
  return status.workflowState === 'Manual Underwriting' || status.workflowState === 'Underwriting';
}

// Filter out the paramed status which we do not want to display in agent account center
export function paramedStatusFilter(examStatus: ExamStatus): boolean {
  return examStatus.StatusEventDetail !== 'Case set to READY TO BILL';
}

export async function fillPIIDataForDownload(clientsRecords: AccountCenterListRecordView[]): Promise<void> {
  const piiKeys: Set<string> = new Set();
  clientsRecords.map((record) => {
    if (record.ownerSSN) {
      piiKeys.add(record.ownerSSN);
    }
    if (record.insuredSSN) {
      piiKeys.add(record.insuredSSN);
    }
  });
  const piiKeyValueMap: Map<string, string> = await SecurityUtils.getPiiValuesByKeys(piiKeys as any);

  clientsRecords.map((record) => {
    if (record.ownerSSN) {
      record.ownerSSN = piiKeyValueMap.get(record.ownerSSN);
    }
    if (record.insuredSSN) {
      record.insuredSSN = piiKeyValueMap.get(record.insuredSSN);
    }
  });
}
